import type { ReactNode } from "react";

/** Lightweight inline SVG icons for admin nav/hubs — no icon-library dependency. */

export type AdminIconName =
  | "dashboard"
  | "tonight"
  | "content"
  | "people"
  | "settings"
  | "checkin"
  | "kiosk"
  | "attendance"
  | "stream"
  | "rsvp"
  | "events"
  | "video"
  | "blog"
  | "gallery"
  | "pages"
  | "copy"
  | "homeVideo"
  | "testimony"
  | "participation"
  | "partner"
  | "message"
  | "subscriber"
  | "comment"
  | "sms"
  | "external"
  | "watching";

const PATHS: Record<AdminIconName, ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  tonight: (
    <>
      <path d="M12 3a9 9 0 1 0 9 9c0-4-3-7-7-8 1 2 1 4 0 6a5 5 0 0 1-6-6c2-1 4-1 4-1z" />
    </>
  ),
  content: (
    <>
      <path d="M4 4h10l6 6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M14 4v6h6" />
      <path d="M8 13h8M8 17h5" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 19c1.2-3.2 3.5-5 6.5-5s5.3 1.8 6.5 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15 14.5c2 .4 3.5 1.6 4.5 4.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2.5M12 19.5V22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M2 12h2.5M19.5 12H22M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" />
    </>
  ),
  checkin: (
    <>
      <path d="M9 11l3 3 7-7" />
      <path d="M4 12a8 8 0 1 0 3-6.3" />
    </>
  ),
  kiosk: (
    <>
      <rect x="5" y="3" width="14" height="14" rx="2" />
      <path d="M9 21h6M12 17v4" />
    </>
  ),
  attendance: (
    <>
      <path d="M8 6h12M8 12h12M8 18h8" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </>
  ),
  stream: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M5.5 8.5a8 8 0 0 0 0 7M18.5 8.5a8 8 0 0 1 0 7" />
      <path d="M2.5 6a12 12 0 0 0 0 12M21.5 6a12 12 0 0 1 0 12" />
    </>
  ),
  rsvp: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
      <path d="M9 15l2 2 4-4" />
    </>
  ),
  events: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 11h18" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="6" width="14" height="12" rx="2" />
      <path d="M17 10l4-2v8l-4-2z" />
    </>
  ),
  blog: (
    <>
      <path d="M5 4h14v16H5z" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  gallery: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="M3 16l5-4 4 3 3-2 6 3" />
    </>
  ),
  pages: (
    <>
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5" />
    </>
  ),
  copy: (
    <>
      <path d="M8 7h11v13H8z" />
      <path d="M5 4h11v3" />
    </>
  ),
  homeVideo: (
    <>
      <path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <path d="M10 14l5 3-5 3z" />
    </>
  ),
  testimony: (
    <>
      <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" />
    </>
  ),
  participation: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.5-4 4-6 7-6s5.5 2 7 6" />
      <path d="M16 4l2 2-2 2" />
    </>
  ),
  partner: (
    <>
      <path d="M8 12h8M12 8v8" />
      <circle cx="12" cy="12" r="9" />
    </>
  ),
  message: (
    <>
      <path d="M4 5h16v11H8l-4 4V5z" />
    </>
  ),
  subscriber: (
    <>
      <path d="M4 6h16v12H4z" />
      <path d="M4 8l8 6 8-6" />
    </>
  ),
  comment: (
    <>
      <path d="M5 5h14v10H9l-4 3V5z" />
      <path d="M8 9h8M8 12h5" />
    </>
  ),
  sms: (
    <>
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M10 18h4" />
      <path d="M9 7h6M9 10h6M9 13h4" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M10 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5" />
    </>
  ),
  watching: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
};

type Props = {
  name: AdminIconName;
  className?: string;
  size?: number;
  title?: string;
};

export function AdminIcon({ name, className, size = 20, title }: Props) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
